/**
 * Pricing Rule Actions Modal Component
 * 
 * Modal for managing Pricing Rule actions - edit and delete.
 * Similar to ItemPriceActionsModal but for Pricing Rule management.
 */

import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ArrowLeft, Trash2, Save } from "lucide-react";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import { frappeMethod } from "@/lib/erp/utils";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LinkFieldSearch } from "@/components/filter-builder/LinkFieldSearch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { MODAL_INPUT_CONSTRAINED_CLASSES, MODAL_LINK_FIELD_SEARCH_CLASSES } from "@/lib/styles/modal";
import type { PricingRule } from "@/lib/erp/pricingRules";

type ModalScreen = "menu" | "edit";

interface PricingRuleActionsModalState {
  screen: ModalScreen;
}

interface PricingRuleActionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pricingRule: PricingRule | null;
  itemCode: string;
}

export function PricingRuleActionsModal({
  open,
  onOpenChange,
  pricingRule,
  itemCode,
}: PricingRuleActionsModalProps) {
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  
  const ui = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      pricingRuleActions: t("pricing_rule_actions", "Pricing Rule Actions"),
      editPricingRule: t("edit_pricing_rule", "Edit Pricing Rule"),
      managePricingRule: t("manage_pricing_rule", "Manage pricing rule"),
      title: t("title", "Title"),
      rateOrDiscount: t("rate_or_discount", "Rate or Discount"),
      discountPercentage: t("discount_percentage", "Discount Percentage"),
      rate: t("rate", "Rate"),
      currency: t("currency", "Currency"),
      validFrom: t("valid_from", "Valid From"),
      validTo: t("valid_to", "Valid To"),
      priority: t("priority", "Priority"),
      customer: t("customer", "Customer"),
      customerGroup: t("customer_group", "Customer Group"),
      company: t("company", "Company"),
      delete: t("delete", "Delete"),
      save: t("save", "Save"),
      cancel: t("cancel", "Cancel"),
      deleteConfirm: t("delete_confirm", "Are you sure you want to delete this pricing rule?"),
      saving: t("saving", "Saving..."),
      deleting: t("deleting", "Deleting..."),
      saved: t("saved", "Saved"),
      deleted: t("deleted", "Deleted"),
      error: t("error", "Error"),
    };
  }, [language]);

  // Modal state machine
  const [state, setState] = useState<PricingRuleActionsModalState>({ screen: "menu" });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateOrDiscountOpen, setRateOrDiscountOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    apply_on: "",
    applicable_for: "",
    price_or_product_discount: "",
    rate_or_discount: "",
    discount_percentage: "",
    rate: "",
    currency: "",
    valid_from: "",
    valid_upto: "",
    priority: "",
    selling: "",
    buying: "",
    customer: "",
    customer_group: "",
    company: "",
    item_code: "",
    item_group: "",
    brand: "",
    warehouse: "",
  });

  // Reset to menu when modal closes
  useEffect(() => {
    if (!open) {
      setState({ screen: "menu" });
      setError(null);
    }
  }, [open]);

  // Initialize form data when pricingRule changes
  useEffect(() => {
    if (pricingRule) {
      try {
        const formatDate = (date: string | null | undefined): string => {
          if (!date) return "";
          try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return "";
            return dateObj.toISOString().split('T')[0];
          } catch {
            return "";
          }
        };

        setFormData({
          title: pricingRule.title || "",
          apply_on: pricingRule.apply_on || "",
          applicable_for: pricingRule.applicable_for || "",
          price_or_product_discount: pricingRule.price_or_product_discount || "",
          rate_or_discount: pricingRule.rate_or_discount || "",
          discount_percentage: pricingRule.discount_percentage?.toString() || "",
          rate: pricingRule.rate?.toString() || "",
          currency: pricingRule.currency || "",
          valid_from: formatDate(pricingRule.valid_from),
          valid_upto: formatDate(pricingRule.valid_upto),
          priority: pricingRule.priority?.toString() || "",
          selling: pricingRule.selling?.toString() || "",
          buying: pricingRule.buying?.toString() || "",
          customer: pricingRule.customer || "",
          customer_group: pricingRule.customer_group || "",
          company: pricingRule.company || "",
          item_code: pricingRule.item_code || "",
          item_group: pricingRule.item_group || "",
          brand: pricingRule.brand || "",
          warehouse: pricingRule.warehouse || "",
        });
      } catch (error) {
        console.error("[PricingRuleActionsModal] Error initializing form data:", error);
        setFormData({
          title: "",
          apply_on: "",
          applicable_for: "",
          price_or_product_discount: "",
          rate_or_discount: "",
          discount_percentage: "",
          rate: "",
          currency: "",
          valid_from: "",
          valid_upto: "",
          priority: "",
          selling: "",
          buying: "",
          customer: "",
          customer_group: "",
          company: "",
          item_code: "",
          item_group: "",
          brand: "",
          warehouse: "",
        });
      }
    }
  }, [pricingRule]);

  // Dynamic modal title based on screen
  const modalTitle = useMemo(() => {
    if (state.screen === "menu") return ui.pricingRuleActions;
    if (state.screen === "edit") return ui.editPricingRule;
    return ui.pricingRuleActions;
  }, [state.screen, ui]);

  // Handle edit action
  const handleEditClick = () => {
    setState({ screen: "edit" });
  };

  // Handle back navigation
  const handleBack = () => {
    setState({ screen: "menu" });
    setError(null);
  };

  // Handle save
  const handleSave = async () => {
    if (!pricingRule) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      // ✅ CORRECT - Fields nested under fieldname
      const payload = {
        doctype: "Pricing Rule",
        name: pricingRule.name,
        fieldname: {
          title: formData.title || null,
          apply_on: formData.apply_on || null,
          applicable_for: formData.applicable_for || null,
          price_or_product_discount: formData.price_or_product_discount || null,
          rate_or_discount: formData.rate_or_discount || null,
          discount_percentage: formData.discount_percentage ? parseFloat(formData.discount_percentage) : null,
          rate: formData.rate ? parseFloat(formData.rate) : null,
          currency: formData.currency || null,
          valid_from: formData.valid_from || null,
          valid_upto: formData.valid_upto || null,
          priority: formData.priority ? parseInt(formData.priority, 10) : null,
          selling: formData.selling ? parseInt(formData.selling, 10) : null,
          buying: formData.buying ? parseInt(formData.buying, 10) : null,
          customer: formData.customer || null,
          customer_group: formData.customer_group || null,
          company: formData.company || null,
          item_code: formData.item_code || null,
          item_group: formData.item_group || null,
          brand: formData.brand || null,
          warehouse: formData.warehouse || null,
        },
      };

      await frappeMethod("frappe.client.set_value", payload);
      
      // ✅ CORRECT - Invalidate with matching query key
      // Query key pattern: ["pricing-rules-for-item", itemCode, pageIndex, pageSize]
      // Use prefix match to invalidate all pages
      queryClient.invalidateQueries({ 
        queryKey: ["pricing-rules-for-item", itemCode] 
      });
      
      // Close modal
      onOpenChange(false);
    } catch (err: any) {
      console.error("[PricingRuleActionsModal] Failed to save:", err);
      setError(err.message || ui.error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!pricingRule) return;
    
    if (!confirm(ui.deleteConfirm)) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await frappeMethod("frappe.client.delete", {
        doctype: "Pricing Rule",
        name: pricingRule.name,
      });
      
      // ✅ Invalidate with matching query key
      queryClient.invalidateQueries({ 
        queryKey: ["pricing-rules-for-item", itemCode] 
      });
      
      // Close modal
      onOpenChange(false);
    } catch (err: any) {
      console.error("[PricingRuleActionsModal] Failed to delete:", err);
      setError(err.message || ui.error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Don't render if no pricingRule
  if (!pricingRule) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(open) => {
      onOpenChange(open);
      // Reset state when modal closes
      if (!open) {
        setState({ screen: "menu" });
        setError(null);
      }
    }}>
      <DialogContent className="max-w-[680px] max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden">
        {/* Top fade-out gradient */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
        
        <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
          <DialogHeader className="px-10 pt-20 pb-4 text-center">
            {/* Back button - only show on edit screen */}
            {state.screen !== "menu" && (
              <div className="absolute top-6 left-6">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="h-10 w-10 rounded-full"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </div>
            )}

            <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
              {modalTitle}
            </DialogTitle>
            <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
              {state.screen === "menu" 
                ? ui.managePricingRule
                : `${ui.editPricingRule} - ${pricingRule?.title || pricingRule?.name || ""}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className={cn(
            "pb-10 px-10",
            state.screen === "edit" ? "pt-0 p-0 flex flex-col flex-1 min-h-0" : "pt-4"
          )}>
            {/* Menu Screen */}
            {state.screen === "menu" && (
              <div className="space-y-4">
                {/* Edit Action */}
                <button
                  type="button"
                  onClick={handleEditClick}
                  className={cn(
                    "w-full flex items-center gap-5 min-h-[60px] px-6 py-4",
                    "bg-gray-50 dark:bg-gray-950 rounded-lg border border-gray-200/50 dark:border-gray-700/50 shadow-sm",
                    "hover:bg-gray-100/80 dark:hover:bg-gray-900/60 transition-all duration-200 ease-out",
                    "active:bg-gray-100 dark:active:bg-gray-900/70"
                  )}
                >
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <Save className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-[17px] font-normal text-gray-900 dark:text-gray-100 leading-[1.35]">
                      {ui.editPricingRule}
                    </div>
                    <div className="text-[15px] text-gray-500 dark:text-gray-400 mt-1 leading-[1.4]">
                      Edit title, rate, discount, dates, and scope
                    </div>
                  </div>
                </button>

                {/* Delete Action */}
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className={cn(
                    "w-full flex items-center gap-5 min-h-[60px] px-6 py-4",
                    "bg-gray-50 dark:bg-gray-950 rounded-lg border border-red-200/50 dark:border-red-800/50 shadow-sm",
                    "hover:bg-red-50/80 dark:hover:bg-red-950/60 transition-all duration-200 ease-out",
                    "active:bg-red-50 dark:active:bg-red-950/70",
                    "disabled:opacity-40 disabled:cursor-not-allowed"
                  )}
                >
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-[17px] font-normal text-red-600 dark:text-red-400 leading-[1.35]">
                      {ui.delete}
                    </div>
                    <div className="text-[15px] text-red-500 dark:text-red-500 mt-1 leading-[1.4]">
                      {isDeleting ? ui.deleting : "Permanently delete this pricing rule"}
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* Edit Screen */}
            {state.screen === "edit" && pricingRule && (
              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
                  <div className="pb-8 px-10 pt-2">
                    {error && (
                      <Alert variant="destructive" className="mb-6">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    {/* Single container with all fields grouped together (like ItemPriceBasicModal) */}
                    <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                      <div className="space-y-0">
                        {/* Title */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="title" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.title}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Input
                              id="title"
                              value={formData.title}
                              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                              placeholder="Pricing Rule Title"
                              className={MODAL_INPUT_CONSTRAINED_CLASSES}
                            />
                          </div>
                        </div>

                        {/* Apply On */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="apply_on" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            Apply On
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype=""
                              referenceDoctype="Pricing Rule"
                              value={formData.apply_on || null}
                              onChange={(value) => setFormData({ ...formData, apply_on: value || "" })}
                              placeholder="Select apply on..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="apply_on"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Applicable For */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="applicable_for" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            Applicable For
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype=""
                              referenceDoctype="Pricing Rule"
                              value={formData.applicable_for || null}
                              onChange={(value) => setFormData({ ...formData, applicable_for: value || "" })}
                              placeholder="Select applicable for..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="applicable_for"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Price or Product Discount */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="price_or_product_discount" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            Price or Product Discount
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype=""
                              referenceDoctype="Pricing Rule"
                              value={formData.price_or_product_discount || null}
                              onChange={(value) => setFormData({ ...formData, price_or_product_discount: value || "" })}
                              placeholder="Select..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="price_or_product_discount"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Rate or Discount Type */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="rate_or_discount" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.rateOrDiscount}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Popover open={rateOrDiscountOpen} onOpenChange={setRateOrDiscountOpen}>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  role="combobox"
                                  className={cn(
                                    MODAL_INPUT_CONSTRAINED_CLASSES,
                                    "flex items-center justify-end gap-2",
                                    "hover:opacity-80 transition-opacity"
                                  )}
                                >
                                  <span className="truncate">
                                    {formData.rate_or_discount || "Select..."}
                                  </span>
                                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent 
                                className="w-[200px] p-0 rounded-xl shadow-2xl backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border border-white/20 dark:border-gray-700/30" 
                                align="end"
                              >
                                <Command className="bg-transparent">
                                  <CommandList>
                                    <CommandEmpty>No option found.</CommandEmpty>
                                    <CommandGroup>
                                      {["Rate", "Discount"].map((option) => (
                                        <CommandItem
                                          key={option}
                                          value={option}
                                          onSelect={() => {
                                            setFormData({ ...formData, rate_or_discount: option });
                                            setRateOrDiscountOpen(false);
                                          }}
                                          className="cursor-pointer"
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              formData.rate_or_discount === option ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          {option}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>

                        {/* Discount Percentage */}
                        {formData.rate_or_discount === "Discount" && (
                          <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                            <label htmlFor="discount_percentage" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                              {ui.discountPercentage}
                            </label>
                            <div className="flex-1 max-w-[60%] flex justify-end">
                              <Input
                                id="discount_percentage"
                                type="number"
                                step="0.01"
                                value={formData.discount_percentage}
                                onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                                placeholder="0.00"
                                className={MODAL_INPUT_CONSTRAINED_CLASSES}
                              />
                            </div>
                          </div>
                        )}

                        {/* Rate */}
                        {formData.rate_or_discount === "Rate" && (
                          <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                            <label htmlFor="rate" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                              {ui.rate}
                            </label>
                            <div className="flex-1 max-w-[60%] flex justify-end">
                              <Input
                                id="rate"
                                type="number"
                                step="0.01"
                                value={formData.rate}
                                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                                placeholder="0.00"
                                className={MODAL_INPUT_CONSTRAINED_CLASSES}
                              />
                            </div>
                          </div>
                        )}

                        {/* Currency */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="currency" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.currency}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype="Currency"
                              referenceDoctype="Pricing Rule"
                              value={formData.currency || null}
                              onChange={(value) => setFormData({ ...formData, currency: value || "" })}
                              placeholder="Select currency..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="currency"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Valid From */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="valid_from" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.validFrom}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Input
                              id="valid_from"
                              type="date"
                              value={formData.valid_from}
                              onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                              className={MODAL_INPUT_CONSTRAINED_CLASSES}
                            />
                          </div>
                        </div>

                        {/* Valid To */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="valid_upto" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.validTo}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Input
                              id="valid_upto"
                              type="date"
                              value={formData.valid_upto}
                              onChange={(e) => setFormData({ ...formData, valid_upto: e.target.value })}
                              className={MODAL_INPUT_CONSTRAINED_CLASSES}
                            />
                          </div>
                        </div>

                        {/* Priority */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="priority" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.priority}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Input
                              id="priority"
                              type="number"
                              step="1"
                              value={formData.priority}
                              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                              placeholder="0"
                              className={MODAL_INPUT_CONSTRAINED_CLASSES}
                            />
                          </div>
                        </div>

                        {/* Selling */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="selling" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            Selling
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Input
                              id="selling"
                              type="number"
                              step="1"
                              value={formData.selling}
                              onChange={(e) => setFormData({ ...formData, selling: e.target.value })}
                              placeholder="0"
                              className={MODAL_INPUT_CONSTRAINED_CLASSES}
                            />
                          </div>
                        </div>

                        {/* Buying */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="buying" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            Buying
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Input
                              id="buying"
                              type="number"
                              step="1"
                              value={formData.buying}
                              onChange={(e) => setFormData({ ...formData, buying: e.target.value })}
                              placeholder="0"
                              className={MODAL_INPUT_CONSTRAINED_CLASSES}
                            />
                          </div>
                        </div>

                        {/* Customer */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="customer" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.customer}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype="Customer"
                              referenceDoctype="Pricing Rule"
                              value={formData.customer || null}
                              onChange={(value) => setFormData({ ...formData, customer: value || "" })}
                              placeholder="Select customer..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="customer"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Customer Group */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="customer_group" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.customerGroup}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype="Customer Group"
                              referenceDoctype="Pricing Rule"
                              value={formData.customer_group || null}
                              onChange={(value) => setFormData({ ...formData, customer_group: value || "" })}
                              placeholder="Select customer group..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="customer_group"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Company */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="company" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.company}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype="Company"
                              referenceDoctype="Pricing Rule"
                              value={formData.company || null}
                              onChange={(value) => setFormData({ ...formData, company: value || "" })}
                              placeholder="Select company..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="company"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Item Code */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="item_code" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            Item Code
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype="Item"
                              referenceDoctype="Pricing Rule"
                              value={formData.item_code || null}
                              onChange={(value) => setFormData({ ...formData, item_code: value || "" })}
                              placeholder="Select item..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="item_code"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Item Group */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="item_group" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            Item Group
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype="Item Group"
                              referenceDoctype="Pricing Rule"
                              value={formData.item_group || null}
                              onChange={(value) => setFormData({ ...formData, item_group: value || "" })}
                              placeholder="Select item group..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="item_group"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Brand */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="brand" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            Brand
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype="Brand"
                              referenceDoctype="Pricing Rule"
                              value={formData.brand || null}
                              onChange={(value) => setFormData({ ...formData, brand: value || "" })}
                              placeholder="Select brand..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="brand"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Warehouse */}
                        <div className="flex items-center justify-between min-h-[52px] px-5">
                          <label htmlFor="warehouse" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            Warehouse
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype="Warehouse"
                              referenceDoctype="Pricing Rule"
                              value={formData.warehouse || null}
                              onChange={(value) => setFormData({ ...formData, warehouse: value || "" })}
                              placeholder="Select warehouse..."
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES)}
                              data-field="warehouse"
                              align="end"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom fade-out gradient */}
                  <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
                </div>

                {/* Sticky Footer Button */}
                <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
                  <Button
                    type="submit"
                    disabled={isSaving}
                    className="w-full text-[17px] font-semibold text-white px-7 py-3.5 h-auto rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-sm hover:shadow-md"
                  >
                    {isSaving ? ui.saving : ui.save}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

